import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import {
  LucideAngularModule,
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bell,
  Box,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Bug,
  Circle,
  CircleCheck,
  CircleDot,
  CircleX,
  Clock,
  Cloud,
  Construction,
  Copy,
  CreditCard,
  Database,
  DollarSign,
  ExternalLink,
  Filter,
  GitBranch,
  GitGraph,
  Github,
  Globe,
  Server,
  Workflow,
  Table,
  Table2,
  Inbox,
  Info,
  LayoutDashboard,
  Link,
  List,
  ListTodo,
  Loader,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Network,
  Pencil,
  Plug2,
  Radio,
  RefreshCw,
  Search,
  Settings,
  ShieldAlert,
  Tag,
  TrendingUp,
  TriangleAlert,
  Unlink,
  User,
  UserPlus,
  Users,
  X,
  Zap,
  Key,
  HardDrive,
  Boxes,
} from 'lucide-angular';

import { AppComponent } from './app.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { authGuard } from './core/guards/auth.guard';

import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { DashboardHomeComponent } from './features/dashboard-home/dashboard-home.component';
import { SelectPlanComponent } from './features/select-plan/select-plan.component';
import { SettingsLayoutComponent } from './features/settings/settings-layout/settings-layout.component';
import { AccountSettingsComponent } from './features/settings/account/account-settings.component';
import { BillingSettingsComponent } from './features/settings/billing/billing-settings.component';
import { IntegrationSettingsComponent } from './features/settings/integrations/integration-settings.component';
import { IntegrationCategoryComponent } from './features/settings/integrations/integration-category.component';
import { WebhookConfigCardComponent } from './features/settings/integrations/webhook-config-card.component';
import { GeographyScopeEditorComponent } from './features/settings/integrations/geography-scope-editor.component';
import { GitHubWorkspaceComponent } from './features/github/github-workspace.component';
import { JiraWorkspaceComponent } from './features/jira/jira-workspace.component';
import { DiagnosisSectionComponent } from './shared/diagnosis-section/diagnosis-section.component';
import { FindingDetailComponent } from './features/findings/finding-detail.component';
import { CloudHubShellComponent } from './features/cloud/cloud-hub-shell.component';
import { ProviderSwitcherComponent } from './features/cloud/provider-switcher.component';
import { AzureOverviewComponent } from './features/cloud/azure-overview.component';
import { AzureServiceDetailComponent } from './features/cloud/azure-service-detail.component';
import { GcpOverviewComponent } from './features/cloud/gcp-overview.component';
import { GcpServiceDetailComponent } from './features/cloud/gcp-service-detail.component';
import { CloudTabsComponent } from './features/cloud/cloud-tabs.component';
import { MonitoredCountBadgeComponent } from './features/cloud/monitored-count-badge.component';
import { StatusBadgeComponent } from './features/cloud/status-badge.component';
import { ServiceCardComponent } from './features/cloud/service-card.component';
import { CloudSummaryBarComponent } from './features/cloud/cloud-summary-bar.component';
import { LambdaDetailComponent } from './features/cloud/lambda-detail.component';
import { GlueDetailComponent } from './features/cloud/glue-detail.component';
import { CodePipelineDetailComponent } from './features/cloud/codepipeline-detail.component';
import { StepFunctionsDetailComponent } from './features/cloud/step-functions-detail.component';
import { EcsDetailComponent } from './features/cloud/ecs-detail.component';
import { RdsDetailComponent } from './features/cloud/rds-detail.component';
import { ApiGatewayDetailComponent } from './features/cloud/api-gateway-detail.component';
import { Ec2DetailComponent } from './features/cloud/ec2-detail.component';
import { CognitoDetailComponent } from './features/cloud/cognito-detail.component';
import { AppSyncDetailComponent } from './features/cloud/appsync-detail.component';
import { DynamoDbDetailComponent } from './features/cloud/dynamodb-detail.component';
import { SqsDetailComponent } from './features/cloud/sqs-detail.component';
import { EventBridgeDetailComponent } from './features/cloud/eventbridge-detail.component';
import { AlbDetailComponent } from './features/cloud/alb-detail.component';
import { SnsDetailComponent } from './features/cloud/sns-detail.component';
import { CloudFrontDetailComponent } from './features/cloud/cloudfront-detail.component';
import { CloudServicePageComponent } from './features/cloud/cloud-service-page.component';
import { CloudMetricsPanelComponent } from './features/cloud/cloud-metrics-panel.component';
import { CloudActivityFeedComponent } from './features/cloud/cloud-activity-feed.component';
import { CloudFilterBarComponent } from './features/cloud/cloud-filter-bar.component';
import { ServiceSettingsDrawerComponent } from './features/cloud/service-settings-drawer.component';
import { ItemSettingsDrawerComponent } from './features/cloud/item-settings-drawer.component';
import { CloudDatePipe } from './core/pipes/cloud-date.pipe';
import { ToastContainerComponent } from './shared/toast-container/toast-container.component';
import { LoginComponent } from './auth/login/login.component';
import { SignupComponent } from './auth/signup/signup.component';
import { ConfirmSignupComponent } from './auth/confirm-signup/confirm-signup.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { MetricCardComponent } from './shared/metric-card/metric-card.component';
import { EmptyStateComponent } from './shared/empty-state/empty-state.component';
import { ComingSoonComponent } from './shared/coming-soon/coming-soon.component';
import { TeamSettingsComponent } from './features/settings/team/team-settings.component';
import { AcceptInviteComponent } from './features/accept-invite/accept-invite.component';
import { MemberPickerComponent } from './shared/member-picker/member-picker.component';
import { NotificationPanelComponent } from './features/notifications/notification-panel.component';
import { BugReportDialogComponent } from './features/bug-report/bug-report-dialog.component';

const routes: Routes = [
  { path: 'login',          component: LoginComponent },
  { path: 'signup',         component: SignupComponent },
  { path: 'confirm-signup', component: ConfirmSignupComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'select-plan',    component: SelectPlanComponent, canActivate: [authGuard] },
  { path: 'accept-invite',  component: AcceptInviteComponent },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '',       component: DashboardHomeComponent, pathMatch: 'full' },
      { path: 'github', component: GitHubWorkspaceComponent },
      { path: 'jira',   component: JiraWorkspaceComponent },
      { path: 'findings/:findingId', component: FindingDetailComponent },
      {
        // Cloud overview — clean full-width category landing page.
        path: 'cloud',
        component: CloudHubShellComponent,
      },
      // Dedicated full-page service detail routes. Each wraps the existing
      // service detail component (rendered as a child route) in shared page
      // chrome (breadcrumb/back + title) via CloudServicePageComponent.
      { path: 'cloud/lambda',         component: CloudServicePageComponent, data: { title: 'Lambda',         icon: 'zap'        }, children: [{ path: '', component: LambdaDetailComponent }] },
      { path: 'cloud/glue',           component: CloudServicePageComponent, data: { title: 'Glue',           icon: 'database'   }, children: [{ path: '', component: GlueDetailComponent }] },
      { path: 'cloud/codepipeline',   component: CloudServicePageComponent, data: { title: 'CodePipeline',   icon: 'git-branch' }, children: [{ path: '', component: CodePipelineDetailComponent }] },
      { path: 'cloud/step-functions', component: CloudServicePageComponent, data: { title: 'Step Functions', icon: 'git-graph'  }, children: [{ path: '', component: StepFunctionsDetailComponent }] },
      { path: 'cloud/ecs',            component: CloudServicePageComponent, data: { title: 'ECS',            icon: 'box'        }, children: [{ path: '', component: EcsDetailComponent }] },
      { path: 'cloud/rds',            component: CloudServicePageComponent, data: { title: 'RDS',            icon: 'database'   }, children: [{ path: '', component: RdsDetailComponent }] },
      { path: 'cloud/api-gateway',    component: CloudServicePageComponent, data: { title: 'API Gateway',    icon: 'globe'      }, children: [{ path: '', component: ApiGatewayDetailComponent }] },
      { path: 'cloud/ec2',            component: CloudServicePageComponent, data: { title: 'EC2',            icon: 'server'     }, children: [{ path: '', component: Ec2DetailComponent }] },
      { path: 'cloud/cognito',        component: CloudServicePageComponent, data: { title: 'Cognito',        icon: 'users'      }, children: [{ path: '', component: CognitoDetailComponent }] },
      { path: 'cloud/appsync',        component: CloudServicePageComponent, data: { title: 'AppSync',        icon: 'workflow'   }, children: [{ path: '', component: AppSyncDetailComponent }] },
      { path: 'cloud/dynamodb',       component: CloudServicePageComponent, data: { title: 'DynamoDB',       icon: 'table'      }, children: [{ path: '', component: DynamoDbDetailComponent }] },
      { path: 'cloud/sqs',            component: CloudServicePageComponent, data: { title: 'SQS',            icon: 'inbox'      }, children: [{ path: '', component: SqsDetailComponent }] },
      { path: 'cloud/eventbridge',    component: CloudServicePageComponent, data: { title: 'EventBridge',    icon: 'radio'      }, children: [{ path: '', component: EventBridgeDetailComponent }] },
      { path: 'cloud/alb',            component: CloudServicePageComponent, data: { title: 'ALB',            icon: 'network'    }, children: [{ path: '', component: AlbDetailComponent }] },
      { path: 'cloud/sns',            component: CloudServicePageComponent, data: { title: 'SNS',            icon: 'megaphone'  }, children: [{ path: '', component: SnsDetailComponent }] },
      { path: 'cloud/cloudfront',     component: CloudServicePageComponent, data: { title: 'CloudFront',     icon: 'cloud'      }, children: [{ path: '', component: CloudFrontDetailComponent }] },
      // Azure detail pages — one generic component, selected by data.id.
      { path: 'cloud/azure-api-management',      component: AzureServiceDetailComponent, data: { id: 'azure-api-management' } },
      { path: 'cloud/azure-application-gateway', component: AzureServiceDetailComponent, data: { id: 'azure-application-gateway' } },
      { path: 'cloud/azure-functions',           component: AzureServiceDetailComponent, data: { id: 'azure-functions' } },
      { path: 'cloud/azure-sql-database',        component: AzureServiceDetailComponent, data: { id: 'azure-sql-database' } },
      { path: 'cloud/azure-container-apps',      component: AzureServiceDetailComponent, data: { id: 'azure-container-apps' } },
      { path: 'cloud/azure-cosmos-db',           component: AzureServiceDetailComponent, data: { id: 'azure-cosmos-db' } },
      { path: 'cloud/azure-data-factory',        component: AzureServiceDetailComponent, data: { id: 'azure-data-factory' } },
      { path: 'cloud/azure-event-grid',          component: AzureServiceDetailComponent, data: { id: 'azure-event-grid' } },
      { path: 'cloud/azure-front-door',          component: AzureServiceDetailComponent, data: { id: 'azure-front-door' } },
      { path: 'cloud/azure-logic-apps',          component: AzureServiceDetailComponent, data: { id: 'azure-logic-apps' } },
      { path: 'cloud/azure-service-bus',         component: AzureServiceDetailComponent, data: { id: 'azure-service-bus' } },
      { path: 'cloud/azure-vm',                  component: AzureServiceDetailComponent, data: { id: 'azure-vm' } },
      { path: 'cloud/azure-app-service',          component: AzureServiceDetailComponent, data: { id: 'azure-app-service' } },
      { path: 'cloud/azure-application-insights', component: AzureServiceDetailComponent, data: { id: 'azure-application-insights' } },
      { path: 'cloud/azure-cache-redis',          component: AzureServiceDetailComponent, data: { id: 'azure-cache-redis' } },
      { path: 'cloud/azure-kubernetes-service',   component: AzureServiceDetailComponent, data: { id: 'azure-kubernetes-service' } },
      { path: 'cloud/azure-event-hubs',           component: AzureServiceDetailComponent, data: { id: 'azure-event-hubs' } },
      { path: 'cloud/azure-key-vault',            component: AzureServiceDetailComponent, data: { id: 'azure-key-vault' } },
      { path: 'cloud/azure-load-balancer',        component: AzureServiceDetailComponent, data: { id: 'azure-load-balancer' } },
      { path: 'cloud/azure-storage-account',      component: AzureServiceDetailComponent, data: { id: 'azure-storage-account' } },
      // GCP detail pages — one generic component, selected by data.id.
      { path: 'cloud/gcp-api-gateway',          component: GcpServiceDetailComponent, data: { id: 'gcp-api-gateway' } },
      { path: 'cloud/gcp-cloud-cdn',            component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-cdn' } },
      { path: 'cloud/gcp-cloud-data-fusion',    component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-data-fusion' } },
      { path: 'cloud/gcp-cloud-load-balancing', component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-load-balancing' } },
      { path: 'cloud/gcp-cloud-run',            component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-run' } },
      { path: 'cloud/gcp-cloud-run-functions',  component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-run-functions' } },
      { path: 'cloud/gcp-cloud-sql',            component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-sql' } },
      { path: 'cloud/gcp-compute-engine',       component: GcpServiceDetailComponent, data: { id: 'gcp-compute-engine' } },
      { path: 'cloud/gcp-eventarc',             component: GcpServiceDetailComponent, data: { id: 'gcp-eventarc' } },
      { path: 'cloud/gcp-firestore',            component: GcpServiceDetailComponent, data: { id: 'gcp-firestore' } },
      { path: 'cloud/gcp-pubsub',               component: GcpServiceDetailComponent, data: { id: 'gcp-pubsub' } },
      { path: 'cloud/gcp-workflows',            component: GcpServiceDetailComponent, data: { id: 'gcp-workflows' } },
      { path: 'cloud/gcp-gke',                  component: GcpServiceDetailComponent, data: { id: 'gcp-gke' } },
      { path: 'cloud/gcp-memorystore-redis',    component: GcpServiceDetailComponent, data: { id: 'gcp-memorystore-redis' } },
      { path: 'cloud/gcp-secret-manager',       component: GcpServiceDetailComponent, data: { id: 'gcp-secret-manager' } },
      { path: 'cloud/gcp-cloud-storage',        component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-storage' } },
      { path: 'cloud/gcp-bigquery',             component: GcpServiceDetailComponent, data: { id: 'gcp-bigquery' } },
      { path: 'cloud/gcp-artifact-registry',    component: GcpServiceDetailComponent, data: { id: 'gcp-artifact-registry' } },
      { path: 'cloud/gcp-cloud-tasks',          component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-tasks' } },
      { path: 'cloud/gcp-cloud-armor',          component: GcpServiceDetailComponent, data: { id: 'gcp-cloud-armor' } },
      // Backward-compatible redirects for the previous /cloud/aws/* URLs
      // (notification deep-links, bookmarks) → the new /cloud/* pages.
      { path: 'cloud/aws',           redirectTo: 'cloud',           pathMatch: 'full' },
      { path: 'cloud/aws/:service',  redirectTo: 'cloud/:service' },
      {
        path: 'settings',
        component: SettingsLayoutComponent,
        children: [
          { path: '',              redirectTo: 'account', pathMatch: 'full' },
          { path: 'account',       component: AccountSettingsComponent },
          { path: 'billing',       component: BillingSettingsComponent },
          { path: 'integrations',  component: IntegrationSettingsComponent },
          { path: 'team',          component: TeamSettingsComponent },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    DashboardHomeComponent,
    SelectPlanComponent,
    SettingsLayoutComponent,
    AccountSettingsComponent,
    BillingSettingsComponent,
    IntegrationSettingsComponent,
    IntegrationCategoryComponent,
    WebhookConfigCardComponent,
    GeographyScopeEditorComponent,
    GitHubWorkspaceComponent,
    JiraWorkspaceComponent,
    DiagnosisSectionComponent,
    FindingDetailComponent,
    CloudHubShellComponent,
    ProviderSwitcherComponent,
    AzureOverviewComponent,
    AzureServiceDetailComponent,
    GcpOverviewComponent,
    GcpServiceDetailComponent,
    CloudTabsComponent,
    MonitoredCountBadgeComponent,
    StatusBadgeComponent,
    ServiceCardComponent,
    CloudSummaryBarComponent,
    LambdaDetailComponent,
    GlueDetailComponent,
    CodePipelineDetailComponent,
    StepFunctionsDetailComponent,
    EcsDetailComponent,
    RdsDetailComponent,
    ApiGatewayDetailComponent,
    Ec2DetailComponent,
    CognitoDetailComponent,
    AppSyncDetailComponent,
    DynamoDbDetailComponent,
    SqsDetailComponent,
    EventBridgeDetailComponent,
    AlbDetailComponent,
    SnsDetailComponent,
    CloudFrontDetailComponent,
    CloudServicePageComponent,
    CloudMetricsPanelComponent,
    CloudActivityFeedComponent,
    CloudFilterBarComponent,
    ServiceSettingsDrawerComponent,
    ItemSettingsDrawerComponent,
    CloudDatePipe,
    ToastContainerComponent,
    LoginComponent,
    SignupComponent,
    ConfirmSignupComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    MetricCardComponent,
    EmptyStateComponent,
    ComingSoonComponent,
    TeamSettingsComponent,
    AcceptInviteComponent,
    MemberPickerComponent,
    NotificationPanelComponent,
    BugReportDialogComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
    LucideAngularModule.pick({
      Activity,
      ArrowDown,
      ArrowLeft,
      ArrowRight,
      ArrowUp,
      Bell,
      Box,
      Check,
      ChevronDown,
      ChevronLeft,
      ChevronRight,
      CircleAlert,
      Bug,
      Circle,
      CircleCheck,
      CircleDot,
      CircleX,
      Clock,
      Cloud,
      Construction,
      Copy,
      CreditCard,
      Database,
      DollarSign,
      ExternalLink,
      Filter,
      GitBranch,
      GitGraph,
      Github,
      Globe,
      Server,
      Workflow,
      Table,
      Table2,
      Inbox,
      Info,
      LayoutDashboard,
      Link,
      List,
      ListTodo,
      Loader,
      Lock,
      LogOut,
      Mail,
      MapPin,
      Megaphone,
      MessageSquare,
      Network,
      Pencil,
      Plug2,
      Radio,
      RefreshCw,
      Search,
      Settings,
      ShieldAlert,
      Tag,
      TrendingUp,
      TriangleAlert,
      Unlink,
      User,
      X,
      UserPlus,
      Users,
      Zap,
      Key,
      HardDrive,
      Boxes,
    }),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
